import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { govtGuides, govtCategories } from '../data/govtGuidesData';
import Navbar from './Navbar';
import Footer from './Footer';
import Calculators from './Calculators';

const GovtGuidesList = () => {
  const [active, setActive] = useState('All');
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    return govtGuides.filter((g) => {
      const matchesCat = active === 'All' || g.category === active;
      const matchesQuery = !query || g.title.toLowerCase().includes(query.toLowerCase()) || g.summary.toLowerCase().includes(query.toLowerCase());
      return matchesCat && matchesQuery;
    });
  }, [active, query]);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-green-50 border-b border-gray-200" data-testid="govt-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-100 text-orange-700 text-xs font-medium mb-4">
            <Icons.Landmark size={14} /> Government Guides · India
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-gray-900 max-w-4xl">
            Definitive guides to Indian government certificates, schemes &amp; rules
          </h1>
          <p className="mt-5 text-base sm:text-lg text-gray-600 max-w-3xl">
            Carefully researched, original walkthroughs covering CGHS, ESIC, PM-JAY, EWS, OBC NCL, CCS Leave Rules, MGNREGA, PMAY, Aadhaar, PAN, DigiLocker and more — written for citizens, employees and applicants.
          </p>

          <div className="mt-8 max-w-2xl relative">
            <Icons.Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides — CGHS, EWS, PM-JAY, NPS, RTI..."
              data-testid="govt-search-input"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <div className="border-b border-gray-200 bg-white sticky top-16 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 overflow-x-auto" data-testid="govt-category-chips">
          <div className="flex items-center gap-2 min-w-max">
            {govtCategories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                data-testid={`govt-cat-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition ${
                  active === cat
                    ? 'bg-gray-900 text-white shadow-sm'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-14">
        <Calculators />

        <div>
          <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900">Full guide library</h2>
              <p className="mt-2 text-base text-gray-600">Browse every guide — schemes, leave rules, certificates and welfare programmes.</p>
            </div>
            <div className="text-sm text-gray-500" data-testid="govt-results-count">
              {filtered.length} guide{filtered.length === 1 ? '' : 's'}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((g) => {
            const Icon = Icons[g.icon] || Icons.FileText;
            return (
              <Link
                key={g.slug}
                to={`/govt-guides/${g.slug}`}
                data-testid={`govt-card-${g.slug}`}
                className="group block bg-white border border-gray-200 rounded-2xl p-6 hover:border-orange-400 hover:shadow-lg transition-all"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-orange-100 to-amber-100 flex items-center justify-center text-orange-700">
                    <Icon size={22} />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs font-medium text-orange-700 mb-1">{g.category}</div>
                    <h3 className="text-base font-semibold text-gray-900 leading-snug group-hover:text-orange-700 transition-colors">
                      {g.title}
                    </h3>
                  </div>
                </div>
                <p className="mt-4 text-sm text-gray-600 line-clamp-3">{g.summary}</p>
                <div className="mt-5 flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Icons.Clock size={12} /> {g.readTime}</span>
                  <span>Updated {g.updated}</span>
                </div>
              </Link>
            );
          })}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GovtGuidesList;
